import { PendingProjectsComponent } from "@/components/pending";
import { ProjectCreateModal } from "@/components/project/project-create-modal";
import { ProjectFilters } from "@/components/project/project-filters";
import { ProjectList } from "@/components/project/project-list";
import { QueryBoundary } from "@/components/query-boundary";
import { Button } from "@papier/ui/components/ui/button";
import { createFileRoute } from "@tanstack/react-router";
import { Folder, Plus } from "lucide-react";
import { useState } from "react";
import { z } from "zod";

const projectSearchSchema = z.object({
  view: z.enum(["grid", "list"]).catch("grid"),
  query: z
    .string()
    .optional()
    .transform((val) => (val?.length === 0 ? undefined : val)),
});

export const Route = createFileRoute("/_app/projects/")({
  component: ProjectsPage,
  loader: ({ context: { queryClient, trpc } }) => {
    queryClient.ensureQueryData(trpc.projects.getAll.queryOptions());
  },
  validateSearch: projectSearchSchema,
  head: () => ({
    meta: [{ title: "Projects" }],
  }),
});

function ProjectsPage() {
  const { useNavigate, useSearch } = Route;
  const navigate = useNavigate();
  const { query } = useSearch({
    select: ({ query }) => ({ query }),
  });
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const openCreateWizard = () => {
    setIsDialogOpen(true);
  };

  const handleResetFilters = () => {
    navigate({
      search: (prev) => ({
        view: prev.view,
      }),
      replace: true,
    });
  };

  const hasActiveFilters = !!query;

  return (
    <main>
      <section className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-foreground flex items-center gap-2 text-2xl font-bold">
            <Folder className="size-5" />
            Projects
          </h1>
          <p className="text-foreground/80">Manage your organization projects</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            onClick={openCreateWizard}
            className="glass-button text-foreground hover:text-foreground border-white/20"
          >
            <Plus className="size-4" />
            Create Project
          </Button>
        </div>
      </section>

      <section className="grid gap-4">
        <ProjectFilters hasActiveFilters={hasActiveFilters} onReset={handleResetFilters} />
        <div className="grid gap-5">
          <QueryBoundary suspenseFallback={<PendingProjectsComponent />}>
            <ProjectList hasActiveFilters={hasActiveFilters} openCreateWizard={openCreateWizard} />
          </QueryBoundary>
        </div>
      </section>

      <ProjectCreateModal open={isDialogOpen} setOpen={setIsDialogOpen} />
    </main>
  );
}
